/**
 * وكيل تنبيهات خفيف فوق sonner.
 *
 * ⚡ سبب وجوده: `import { toast } from "sonner"` داخل ملفّات تعمل من أوّل
 * تحميل (cartStore / shopify / share-*) كان يسحب حزمة sonner كاملة إلى
 * ملفّ الإقلاع (~33 ك.ب من العمل على المعالج) قبل أن يظهر أيّ تنبيه.
 * هنا نُحمّل sonner عند أوّل تنبيه فعليّ فقط.
 *
 * الاستخدام مطابق تماماً لـ sonner: toast(...) · toast.success(...) …
 * والدوالّ التي تُرجع مُعرّفاً (loading) تُرجع مُعرّفاً نولّده نحن ونمرّره
 * لـ sonner، فيبقى `toast.dismiss(id)` و`{ id }` يعملان كما هما.
 */

type AnyArgs = unknown[];

let mod: typeof import("sonner") | null = null;
let pending: Promise<typeof import("sonner")> | null = null;

function ensure() {
  if (!pending) pending = import("sonner").then((m) => ((mod = m), m));
  return pending;
}

function run(fn: (t: typeof import("sonner").toast) => void) {
  if (mod) fn(mod.toast);
  else void ensure().then((m) => fn(m.toast));
}

function withId(args: AnyArgs): string | number {
  const opts = args[1] as Record<string, unknown> | undefined;
  if (opts && typeof opts === "object" && opts.id != null) return opts.id as string | number;
  const id = `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  args[1] = { ...(opts || {}), id };
  return id;
}

function invoke(method: string | null, args: AnyArgs) {
  const id = withId(args);
  run((t) => {
    const fn = method ? (t as unknown as Record<string, unknown>)[method] : t;
    if (typeof fn === "function") (fn as (...a: AnyArgs) => unknown)(...args);
  });
  return id;
}

const base = (...args: AnyArgs) => invoke(null, args);

export const toast = Object.assign(base, {
  success: (...args: AnyArgs) => invoke("success", args),
  error: (...args: AnyArgs) => invoke("error", args),
  info: (...args: AnyArgs) => invoke("info", args),
  warning: (...args: AnyArgs) => invoke("warning", args),
  message: (...args: AnyArgs) => invoke("message", args),
  loading: (...args: AnyArgs) => invoke("loading", args),
  custom: (...args: AnyArgs) => invoke("custom", args),
  dismiss: (...args: AnyArgs) => {
    run((t) => t.dismiss(...(args as [])));
  },
  promise: (...args: AnyArgs) => {
    run((t) => (t.promise as (...a: AnyArgs) => unknown)(...args));
  },
});
